import { useCallback } from 'react';
import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import { CatalogItemOwnership } from 'fym-shared/src/smartlinks/catalog';
import { useClaimCatalogItemMutation } from '../../../redux/api/fymAPI';
import { CatalogClaimResponse } from '../../../redux/api/fymAPI.types';
import { ErrorAPIResult, isSuccessfulResult } from '../../../redux/api/utils/isSuccessfulResult';
import { CatalogClaimSnapshot } from './types';

/** A claim the account already holds. Not a failure — the row is where it should be. */
export function isAlreadyClaimedError(error: ErrorAPIResult['error'] | undefined): boolean {
  return error instanceof FYMAPIError && error.statusCode === 409;
}

interface UseClaimReleaseProps {
  accountId: number;
}

/**
 * One tap, no permission needed. The snapshot travels with the call because the
 * app already holds the track's details, and a second fetch server-side would
 * put a network hop behind a button that has to feel instant.
 */
export function useClaimRelease({ accountId }: UseClaimReleaseProps) {
  const [claimCatalogItem, { isLoading, error, reset }] = useClaimCatalogItemMutation();

  /**
   * Resolves with the claim answer (the saved row, and the merge facts of design catalog-merge a4),
   * or null when the claim failed. `linkToItemUuid` links the track to
   * that unreleased row instead of adding a new row (design g3). `keepSeparate` is the artist's
   * "add it as a new song": the server does not merge the claim into a row of the same song.
   * `mergeIntoItemUuid` is "Merge" on a suggestion (design catalog-merge c1-c3): the release joins
   * that row and no row is added.
   */
  const claim = useCallback(
    async (
      snapshot: CatalogClaimSnapshot,
      ownership: CatalogItemOwnership,
      linkToItemUuid?: string,
      keepSeparate?: boolean,
      mergeIntoItemUuid?: string
    ): Promise<CatalogClaimResponse | null> => {
      const result = await claimCatalogItem({
        accountId,
        ownership,
        ...snapshot,
        linkToItemUuid,
        keepSeparate,
        mergeIntoItemUuid,
      });
      return isSuccessfulResult(result) ? result.data : null;
    },
    [accountId, claimCatalogItem]
  );

  return { claim, isLoading, error, reset, isAlreadyClaimed: isAlreadyClaimedError(error) };
}
